import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { exploreApi } from '../../api/exploreApi';
import { useAuth } from '../../auth/AuthContext';
import { authReturnLocation } from '../../auth/authReturn';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import OverflowMenu from '../../components/OverflowMenu';
import { useAnchoredMenuPosition } from '../../components/useAnchoredMenuPosition';
import { shareSocialPost } from './shareSocialPost';
import { SOCIAL_ACTION_PRODUCT_CHROME as socialChrome } from './socialActionPresentation';
import {
  SocialBookmarkIcon,
  SocialCommentIcon,
  SocialHeartIcon,
  SocialMoreIcon,
  SocialRepostIcon,
} from './SocialActionIcons';
import './post-engagement-bar.css';

const LIKE_SAVE_FAILED = '操作失敗，請稍後再試。';
const SHARE_FAILED = '無法分享這則貼文，請稍後再試。';

/**
 * 共用貼文互動列：按讚 → 留言 → 轉發／引用 → 收藏 → 更多。
 * 分享移至「更多」；轉發使用系統分享或複製連結；不捏造 quote／share／repost 計數。
 */
export default function PostEngagementBar({
  postId,
  engagement,
  detailTo,
  detailState,
  commentHref,
  onOpenComments,
  shareTitle = 'Travel story',
  onEngagementChange,
  moreItems = [],
  quoteAllowed = true,
  className = '',
}) {
  const { isAuthenticated } = useAuth();
  const { t } = useTravelTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(null);
  const [actionError, setActionError] = useState('');
  const [repostMenuOpen, setRepostMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const repostWrapRef = useRef(null);
  const repostTriggerRef = useRef(null);
  const repostMenuRef = useRef(null);
  const repostMenuId = useId();
  // 互動列為 overflow-x: auto，外層卡片也會裁切；選單須以 fixed 錨定才點得到
  const repostMenuStyle = useAnchoredMenuPosition(repostMenuOpen, repostTriggerRef, repostMenuRef);

  const liked = Boolean(engagement?.liked);
  const saved = Boolean(engagement?.saved);
  const likeCount = Number(engagement?.likeCount) || 0;
  const saveCount = Number(engagement?.saveCount) || 0;
  const commentCount = Number(engagement?.commentCount) || 0;
  const detailPath = detailTo || `/social/posts/${postId}`;
  const commentTarget = commentHref?.startsWith('#')
    ? `${detailPath}${commentHref}`
    : (commentHref || `${detailPath}#comments`);
  const interactionLocked = Boolean(busy);

  useEffect(() => {
    if (!repostMenuOpen) return undefined;
    function onPointerDown(event) {
      if (repostWrapRef.current && !repostWrapRef.current.contains(event.target)) {
        setRepostMenuOpen(false);
      }
    }
    function onEscape(event) {
      if (event.key === 'Escape') setRepostMenuOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [repostMenuOpen]);

  function requireAuth() {
    if (isAuthenticated) return true;
    navigate('/login', {
      state: {
        from: authReturnLocation(location),
        notice: t('travel.discoverGuest.engagementNotice'),
      },
    });
    return false;
  }

  async function mutate(type) {
    if (!requireAuth() || busy) return;
    const active = type === 'liked' ? liked : saved;
    const api = type === 'liked'
      ? (active ? exploreApi.unlikePost : exploreApi.likePost)
      : (active ? exploreApi.unsavePost : exploreApi.savePost);
    setBusy(type);
    setActionError('');
    try {
      const result = await api(postId);
      if (result?.changed) onEngagementChange?.(postId, type, !active);
    } catch {
      setActionError(LIKE_SAVE_FAILED);
    } finally {
      setBusy(null);
    }
  }

  async function share() {
    if (!requireAuth() || busy) return;
    setBusy('share');
    setActionError('');
    setMoreMenuOpen(false);
    setRepostMenuOpen(false);
    try {
      const result = await shareSocialPost(postId, {
        shareApi: exploreApi.sharePost,
        title: shareTitle,
      });
      if (!result.shared && result.method !== 'web-share-cancelled') {
        setActionError(SHARE_FAILED);
      }
    } catch {
      setActionError(SHARE_FAILED);
    } finally {
      setBusy(null);
    }
  }

  function quote() {
    if (!requireAuth() || busy || !quoteAllowed) return;
    setRepostMenuOpen(false);
    navigate('/social/posts/new', { state: { quotedSocialPostId: String(postId) } });
  }

  function toggleRepostMenu() {
    if (interactionLocked) return;
    setMoreMenuOpen(false);
    setRepostMenuOpen((open) => !open);
  }

  function handleMoreMenuOpenChange(next) {
    if (next && interactionLocked) return;
    if (next) setRepostMenuOpen(false);
    setMoreMenuOpen(next);
  }

  return (
    <>
      <footer className={`post-engagement-bar ${className}`.trim()} data-testid={`post-engagement-bar-${postId}`}>
        <button
          type="button"
          className="post-engagement-bar__action"
          data-testid="post-engagement-like"
          aria-label={liked ? socialChrome.unlikeAria : socialChrome.likeAria}
          aria-pressed={liked}
          disabled={interactionLocked}
          onClick={() => mutate('liked')}
        >
          <SocialHeartIcon className="post-engagement-bar__icon" filled={liked} />
          <span className="post-engagement-bar__count">{likeCount}</span>
        </button>

        <Link
          className="post-engagement-bar__action post-engagement-bar__comment"
          data-testid="post-engagement-comment"
          onClick={onOpenComments ? (event) => { event.preventDefault(); onOpenComments(); } : undefined}
          to={commentTarget}
          state={detailState}
          aria-label={socialChrome.readCommentsAria(commentCount)}
        >
          <SocialCommentIcon className="post-engagement-bar__icon" />
          <span className="post-engagement-bar__count">{commentCount}</span>
        </Link>

        <div className="post-engagement-bar__menu-wrap" ref={repostWrapRef}>
          <button
            ref={repostTriggerRef}
            type="button"
            className="post-engagement-bar__action"
            data-testid="post-engagement-repost"
            aria-label={socialChrome.repostMenuAria}
            aria-expanded={repostMenuOpen}
            aria-controls={repostMenuId}
            disabled={interactionLocked}
            onClick={toggleRepostMenu}
          >
            <SocialRepostIcon className="post-engagement-bar__icon" />
          </button>
          {repostMenuOpen ? (
            <div
              id={repostMenuId}
              ref={repostMenuRef}
              className="post-engagement-bar__menu"
              role="menu"
              data-testid="post-engagement-repost-menu"
              style={repostMenuStyle}
            >
              <button type="button" role="menuitem" disabled={interactionLocked} onClick={share}>
                {socialChrome.repost}
              </button>
              {quoteAllowed ? (
                <button
                  type="button"
                  role="menuitem"
                  aria-label={socialChrome.quoteAria}
                  disabled={interactionLocked}
                  onClick={quote}
                >
                  {socialChrome.quote}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <button
          type="button"
          className="post-engagement-bar__action"
          data-testid="post-engagement-save"
          aria-label={saved ? socialChrome.unsaveAria : socialChrome.saveAria}
          aria-pressed={saved}
          disabled={interactionLocked}
          onClick={() => mutate('saved')}
        >
          <SocialBookmarkIcon className="post-engagement-bar__icon" filled={saved} />
          <span className="post-engagement-bar__count">{saveCount}</span>
        </button>

        <OverflowMenu
          items={[{ id: 'share', label: socialChrome.share, onSelect: share }, ...moreItems]}
          label={socialChrome.moreMenuAria}
          triggerContent={<SocialMoreIcon className="post-engagement-bar__icon" />}
          disabled={interactionLocked}
          anchored
          sheetOnNarrow
          open={moreMenuOpen}
          onOpenChange={handleMoreMenuOpenChange}
          wrapClassName="post-engagement-bar__menu-wrap"
          triggerClassName="post-engagement-bar__action"
          menuClassName="post-engagement-bar__menu"
          triggerTestId="post-engagement-more"
          menuTestId="post-engagement-more-menu"
        />
      </footer>
      {actionError ? <p className="post-engagement-bar__error" role="alert">{actionError}</p> : null}
    </>
  );
}
